import { useState } from 'react'
import {
  Key,
  Plus,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Shield,
  RotateCcw,
} from 'lucide-react'
import styles from './CertificatesPage.module.css'

interface Certificate {
  id: string
  domain: string
  issuer: string
  validFrom: string
  expires: string
  daysLeft: number
  autoRenew: boolean
  wildcard: boolean
  environment: string
  status: 'valid' | 'expiring' | 'expired'
}

const MOCK_CERTS: Certificate[] = [
  { id: 'cert-001', domain: '*.aptean.app',           issuer: "Let's Encrypt", validFrom: '2026-01-09', expires: '2026-07-09', daysLeft: 91,  autoRenew: true,  wildcard: true,  environment: 'production', status: 'valid'    },
  { id: 'cert-002', domain: 'api.aptean.app',          issuer: "Let's Encrypt", validFrom: '2026-03-01', expires: '2026-06-01', daysLeft: 53,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
  { id: 'cert-003', domain: 'aptean.com',              issuer: 'DigiCert',       validFrom: '2025-04-09', expires: '2026-04-09', daysLeft: 0,   autoRenew: false, wildcard: false, environment: 'production', status: 'expiring' },
  { id: 'cert-004', domain: 'www.aptean.com',          issuer: 'DigiCert',       validFrom: '2025-04-09', expires: '2026-04-12', daysLeft: 3,   autoRenew: false, wildcard: false, environment: 'production', status: 'expiring' },
  { id: 'cert-005', domain: 'mcp.aptean.app',          issuer: "Let's Encrypt", validFrom: '2026-02-15', expires: '2026-05-16', daysLeft: 37,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
  { id: 'cert-006', domain: 'demo.aptean.app',          issuer: "Let's Encrypt", validFrom: '2026-03-20', expires: '2026-06-20', daysLeft: 72,  autoRenew: true,  wildcard: false, environment: 'demo',       status: 'valid'    },
  { id: 'cert-007', domain: 'grafana.aptean.app',      issuer: "Let's Encrypt", validFrom: '2026-03-25', expires: '2026-06-25', daysLeft: 77,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
  { id: 'cert-008', domain: 'docs.aptean.com',         issuer: "Let's Encrypt", validFrom: '2026-04-01', expires: '2026-07-01', daysLeft: 83,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
  { id: 'cert-009', domain: 'pay-edi.aptean.app',      issuer: "Let's Encrypt", validFrom: '2026-01-20', expires: '2026-04-20', daysLeft: 11,  autoRenew: true,  wildcard: false, environment: 'production', status: 'expiring' },
  { id: 'cert-010', domain: 'agent.aptean.app',        issuer: "Let's Encrypt", validFrom: '2026-04-05', expires: '2026-07-05', daysLeft: 87,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
  { id: 'cert-011', domain: 'dev.aptean.app',          issuer: "Let's Encrypt", validFrom: '2026-03-10', expires: '2026-06-10', daysLeft: 62,  autoRenew: true,  wildcard: false, environment: 'dev',        status: 'valid'    },
  { id: 'cert-012', domain: 'erpcloud.aptean.com',     issuer: 'DigiCert',       validFrom: '2025-06-01', expires: '2026-06-01', daysLeft: 53,  autoRenew: true,  wildcard: false, environment: 'production', status: 'valid'    },
]

function getDaysClass(days: number): string {
  if (days <= 14) return 'daysRed'
  if (days <= 30) return 'daysAmber'
  return 'daysGreen'
}

export function CertificatesPage() {
  const [certs, setCerts] = useState(MOCK_CERTS)
  const [showModal, setShowModal] = useState(false)
  const [envFilter, setEnvFilter] = useState('all')

  const toggleAutoRenew = (id: string) => {
    setCerts((prev) => prev.map((c) => c.id === id ? { ...c, autoRenew: !c.autoRenew } : c))
  }

  const filtered = envFilter === 'all' ? certs : certs.filter((c) => c.environment === envFilter)

  const stats = {
    total: certs.filter((c) => c.status !== 'expired').length,
    expiring: certs.filter((c) => c.daysLeft <= 30).length,
    autoRenew: certs.filter((c) => c.autoRenew).length,
    wildcards: certs.filter((c) => c.wildcard).length,
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Key size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Certificate Management</h1>
            <p className={styles.pageSubtitle}>Monitor SSL/TLS certificate validity, auto-renewal, and expiration alerts</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary}>
            <RefreshCw size={14} />
            Refresh
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowModal(true)}>
            <Plus size={15} />
            Add Certificate
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--aptean-success-bg)', color: 'var(--aptean-success)' }}>
            <Shield size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Active Certificates</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={`${styles.statCard2} ${styles.alertCard}`}>
          </div>
          <div className={styles.statIcon} style={{ background: stats.expiring > 0 ? 'var(--aptean-error-bg)' : 'var(--aptean-success-bg)', color: stats.expiring > 0 ? 'var(--aptean-error)' : 'var(--aptean-success)' }}>
            <AlertTriangle size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.expiring}</div>
            <div className={styles.statLabel}>Expiring Soon (&lt;30d)</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-infra-bg)', color: 'var(--dc-infra)' }}>
            <RotateCcw size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.autoRenew}</div>
            <div className={styles.statLabel}>Auto-Renew Enabled</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'var(--dc-ops-bg)', color: 'var(--dc-ops)' }}>
            <Key size={18} />
          </div>
          <div>
            <div className={styles.statValue}>{stats.wildcards}</div>
            <div className={styles.statLabel}>Wildcard Certs</div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className={styles.filterBar}>
        <div className={styles.filterLabel}>Filter by Environment:</div>
        {(['all', 'production', 'demo', 'dev', 'uat_usgov', 'prod_usgov']).map((env) => (
          <button
            key={env}
            className={`${styles.filterBtn} ${envFilter === env ? styles.filterBtnActive : ''}`}
            onClick={() => setEnvFilter(env)}
          >
            {env === 'all' ? 'All' : env === 'uat_usgov' ? 'USGOV UAT' : env === 'prod_usgov' ? 'USGOV Prod' : env}
          </button>
        ))}
        <span className={styles.filterCount}>{filtered.length} certificates</span>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Domain</th>
                <th>Issuer</th>
                <th>Valid From</th>
                <th>Expires</th>
                <th>Days Left</th>
                <th>Environment</th>
                <th>Auto-Renew</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cert) => {
                const daysClass = getDaysClass(cert.daysLeft)
                return (
                  <tr key={cert.id} className={`${styles.tableRow} ${cert.daysLeft <= 14 ? styles.tableRowAlert : ''}`}>
                    <td>
                      <div className={styles.domainCell}>
                        {cert.wildcard && <span className={styles.wildcardBadge}>WILDCARD</span>}
                        <span className={styles.domainName}>{cert.domain}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.issuerBadge}>{cert.issuer}</span>
                    </td>
                    <td className={styles.cellMuted}>{cert.validFrom}</td>
                    <td className={styles.cellMuted}>{cert.expires}</td>
                    <td>
                      <div className={`${styles.daysCell} ${styles[daysClass]}`}>
                        {cert.daysLeft <= 14 && <AlertTriangle size={12} />}
                        {cert.daysLeft > 14 && cert.daysLeft <= 30 && <Clock size={12} />}
                        {cert.daysLeft > 30 && <CheckCircle2 size={12} />}
                        {cert.daysLeft === 0 ? 'Today!' : `${cert.daysLeft}d`}
                      </div>
                    </td>
                    <td>
                      <span className={styles.envBadge}>{cert.environment}</span>
                    </td>
                    <td>
                      <button
                        className={`${styles.toggleBtn} ${cert.autoRenew ? styles.toggleBtnOn : styles.toggleBtnOff}`}
                        onClick={() => toggleAutoRenew(cert.id)}
                        title={cert.autoRenew ? 'Disable auto-renew' : 'Enable auto-renew'}
                      >
                        <span className={styles.toggleKnob} />
                        <span className={styles.toggleLabel}>{cert.autoRenew ? 'On' : 'Off'}</span>
                      </button>
                    </td>
                    <td>
                      <div className={styles.actionRow}>
                        <button className={styles.actionBtn} title="Renew now">
                          <RotateCcw size={13} />
                        </button>
                        <button className={styles.actionBtnSecondary} title="View details">
                          <Shield size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Certificate Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Add Certificate</h2>
            <p className={styles.modalSubtitle}>Import or provision a new TLS certificate</p>
            <div className={styles.formGrid}>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Domain</label>
                <input className={styles.formInput} placeholder="e.g. api.aptean.app or *.aptean.app" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Issuer</label>
                <select className={styles.formSelect}>
                  <option>Let's Encrypt</option>
                  <option>DigiCert</option>
                  <option>Self-signed</option>
                  <option>Import existing</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Environment</label>
                <select className={styles.formSelect}>
                  <option>production</option>
                  <option>demo</option>
                  <option>dev</option>
                  <option value="uat_usgov">USGOV UAT</option>
                  <option value="prod_usgov">USGOV Prod</option>
                </select>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              <button className={styles.btnPrimary} onClick={() => setShowModal(false)}>
                <Key size={14} />
                Provision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
